// The persistent frame: ambient background, scroll region, and navigation.
//
// On phones the nav is the floating pill from the design handoff. At >=1024px the same markup
// becomes a left rail (see shell.css) — one component, two layouts, no duplicated nav logic.

import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  IconBook,
  IconCollapse,
  IconCompass,
  IconGear,
  IconHome,
  IconImage,
  IconOreia,
  IconShards,
  IconSpark,
  IconUser,
  OreiaMark,
} from './Icons';

const RAIL_KEY = 'oreia.nav.rail';

function railStartsCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

/** The four primary destinations, matching the iPhone tab bar exactly. */
const NAV_ITEMS = [
  { to: '/', label: 'Home', Icon: IconHome },
  { to: '/journal', label: 'Journal', Icon: IconBook },
  { to: '/oreia', label: 'Oreia', Icon: IconOreia },
  { to: '/self', label: 'Self', Icon: IconUser },
] as const;

/**
 * Secondary destinations. On iPhone these are sheets pushed from Home and Self; a browser has real
 * URLs and a wider frame, so they get their own routes — reachable from the rail on desktop and a
 * chip row on phones rather than crowding the four-item pill.
 */
const SECONDARY_ITEMS = [
  { to: '/discover', label: 'Discover', Icon: IconCompass },
  { to: '/mirror', label: 'Mirror', Icon: IconShards },
  { to: '/dreams', label: 'Dreams', Icon: IconImage },
  { to: '/settings', label: 'Settings', Icon: IconGear },
] as const;

/**
 * "Beta", and a way to act on it.
 *
 * Rendered twice on purpose — once in the desktop rail and once in the phone chip row — because the
 * two pieces of shell chrome are mutually exclusive at every width (`.orail-brand` is hidden below
 * 1024px, `.osubnav` above it), so exactly one is ever on screen. The alternative, a single element
 * repositioned by media query, would have to live in one of those two containers anyway.
 *
 * It leaves the app rather than opening a composer. The form is on the marketing site because that is
 * where Turnstile already lives and where someone without an account can also reach it; carrying the
 * current route in `?page=` is what keeps a report from the app actionable. The better version for
 * signed-in people is an in-app composer posting to the app's own API, where the session is already
 * proof of a human and no third-party challenge script is needed at all — that is the follow-up.
 */
function BetaChip({ className, page }: { className: string; page: string }) {
  return (
    <a
      className={className}
      href={`https://oreiathejournal.com/?page=${encodeURIComponent(page)}#feedback`}
      target="_blank"
      rel="noreferrer"
      title="Oreia is in open beta — tell us what is missing"
    >
      Beta
    </a>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [railCollapsed, setRailCollapsed] = useState(railStartsCollapsed);

  useEffect(() => {
    localStorage.setItem(RAIL_KEY, railCollapsed ? 'collapsed' : 'open');
  }, [railCollapsed]);

  return (
    <div className="oapp oscreen" data-rail={railCollapsed ? 'collapsed' : 'open'}>
      <div className="oscroll">
        <div className="ocolumn">
          {/* Phone-only: the secondary destinations as a scrollable chip row, so the four-item
              pill stays uncrowded. Hidden at >=1024px, where the rail carries them instead. */}
          <nav className="osubnav" aria-label="More">
            <BetaChip className="ochip obeta" page={location.pathname} />
            {SECONDARY_ITEMS.map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `ochip${isActive ? ' active' : ''}`}
              >
                <Icon style={{ width: 14, height: 14 }} />
                {label}
              </NavLink>
            ))}
          </nav>

          {children}
        </div>
      </div>

      <div className={`onav-wrap${railCollapsed ? ' is-collapsed' : ''}`}>
        {/* The app icon alone, no wordmark. It is the mark people already have on their home
            screen, so it identifies the app faster than "Oreia" set in a serif does — and it
            leaves the rail's top row to the one control that belongs there. */}
        <div className="orail-brand">
          <OreiaMark />
          <BetaChip className="obeta" page={location.pathname} />
          {/* Desktop only — the phone layout is the bottom pill, which is already as small as it
              should get. In the top row beside the mark, which is where the rail's own control
              belongs and the only thing left in that row once the rest folds away. */}
          <button
            className="orail-toggle"
            type="button"
            onClick={() => setRailCollapsed((collapsed) => !collapsed)}
            aria-expanded={!railCollapsed}
            aria-label={railCollapsed ? 'Show navigation' : 'Hide navigation'}
          >
            <IconCollapse />
          </button>
        </div>

        <nav className="onav" aria-label="Main">
          {NAV_ITEMS.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => `onav-item${isActive ? ' active' : ''}`}
            >
              <span className="iw">
                <Icon />
              </span>
              {label}
            </NavLink>
          ))}
        </nav>

        <nav className="orail-secondary" aria-label="More">
          {SECONDARY_ITEMS.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `onav-item${isActive ? ' active' : ''}`}
            >
              <span className="iw">
                <Icon />
              </span>
              {label}
            </NavLink>
          ))}
        </nav>

        <button
          className="oorb"
          // Carry the current location so closing the composer returns here rather than to Home.
          onClick={() => navigate('/write', { state: { from: location.pathname } })}
          aria-label="New entry"
        >
          <IconSpark />
          <span className="orb-label">New entry</span>
        </button>
      </div>
    </div>
  );
}
